import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { topicById, topicForRoute, type HelpTopic } from './content';

/**
 * Who has the help panel open, and at which topic.
 *
 * One provider above the shell, so the `?` in the top bar and every inline `?` beside a panel
 * drive the same surface. Two independent help mechanisms would be two places for the
 * explanation to differ.
 */
interface HelpContextValue {
  topic: HelpTopic | null;
  /** Open at a specific topic; omit the id to open at the one for the current route. */
  open(id?: string): void;
  close(): void;
  toggle(): void;
}

const HelpContext = createContext<HelpContextValue | null>(null);

export function HelpProvider({ children }: { children: ReactNode }) {
  const [topic, setTopic] = useState<HelpTopic | null>(null);
  const location = useLocation();

  const open = useCallback(
    (id?: string) => {
      // An unknown id falls back to the route's topic rather than opening an empty panel. The
      // test asserts no such id exists, so this is the belt to that braces.
      setTopic((id ? topicById(id) : null) ?? topicForRoute(location.pathname));
    },
    [location.pathname],
  );
  const close = useCallback(() => setTopic(null), []);
  const toggle = useCallback(() => {
    setTopic((current) => (current ? null : topicForRoute(location.pathname)));
  }, [location.pathname]);

  const value = useMemo(() => ({ topic, open, close, toggle }), [topic, open, close, toggle]);
  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelp(): HelpContextValue {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error('useHelp must be used inside a HelpProvider');
  return ctx;
}
